import { useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Check, Lock, MousePointer2, Upload, Rocket } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import { EASE } from "./motion";

const STEPS = [
  { n: "01", icon: Upload, title: "Importe o HTML", desc: "Cole o código que a IA gerou ou faça upload do .html. Imagens em base64 são extraídas e otimizadas na hora." },
  { n: "02", icon: MousePointer2, title: "Edite clicando", desc: "Clique em qualquer texto e digite. Redimensione arrastando, estilo Canva. Painel de estilos completo, desktop e celular separados." },
  { n: "03", icon: Rocket, title: "Publique com 1 clique", desc: "A página vai pro ar num link HTTPS na hora, com SSL e CDN. Conecte seu domínio quando quiser." },
];

const CODE = ["<section class=\"hero\">", "  <h1>Comece a recuperar</h1>", "  <p>Protocolo de 21 dias…</p>", "  <a class=\"cta\">Quero</a>", "</section>"];

const VisualImport = () => (
  <div className="w-full card rounded-3xl overflow-hidden">
    <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50">
      <span className="h-3 w-3 rounded-full bg-slate-300" /><span className="h-3 w-3 rounded-full bg-slate-300" /><span className="h-3 w-3 rounded-full bg-slate-300" />
      <span className="ml-3 font-mono text-xs text-slate-500">colar-html.html</span>
    </div>
    <div className="p-6 font-mono text-[13px] sm:text-sm leading-7 text-slate-700 min-h-[220px]">
      {CODE.map((l, i) => (
        <motion.div key={l} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.12, ease: EASE }}>
          <span className="text-slate-300 select-none mr-4">{i + 1}</span>
          <span className={l.includes("cta") ? "text-[var(--orange)]" : l.includes("h1") ? "text-[var(--blue)]" : ""}>{l}</span>
        </motion.div>
      ))}
      <motion.span className="inline-block w-2 h-5 bg-[var(--blue)] cursor-blink align-middle" />
    </div>
    <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.9, ease: EASE }} className="px-6 pb-6 flex items-center justify-between gap-4">
      <span className="text-xs text-slate-500">3 imagens base64 extraídas e otimizadas</span>
      <span className="btn btn-primary !py-2.5 !px-4 text-xs">Importar</span>
    </motion.div>
  </div>
);

const VisualEdit = () => (
  <div className="w-full card rounded-3xl overflow-hidden relative">
    <img src={ASSETS.editor} alt="Editor visual do Lowpages" loading="lazy" decoding="async" className="w-full h-auto object-cover object-top max-h-[420px]" />
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, ease: EASE }}
      className="absolute left-[1.5%] top-[21%] w-[42%] h-[24%] border-2 border-[var(--blue)] rounded-md"
    >
      {[["-left-1.5", "-top-1.5"], ["-right-1.5", "-top-1.5"], ["-left-1.5", "-bottom-1.5"], ["-right-1.5", "-bottom-1.5"]].map(([x, y]) => (
        <span key={x + y} className={`absolute ${x} ${y} h-3 w-3 rounded-sm bg-white border-2 border-[var(--blue)]`} />
      ))}
      <span className="absolute -top-7 left-0 bg-[var(--blue)] text-white font-mono text-[10px] px-2 py-1 rounded">h1 · 64px · Serif</span>
    </motion.div>
    <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6, ease: EASE }} className="absolute right-4 top-4 glass rounded-2xl p-4 w-52 shadow-xl text-xs">
      <div className="font-semibold text-[var(--ink)] mb-3">Tipografia</div>
      {[["Fonte", "Fraunces"], ["Tamanho", "64px"], ["Cor", "#1F4B3A"]].map(([k, v]) => (
        <div key={k} className="flex justify-between py-1.5 border-t border-slate-100 text-slate-600"><span>{k}</span><span className="font-mono text-[var(--ink)]">{v}</span></div>
      ))}
    </motion.div>
  </div>
);

const VisualPublish = () => (
  <div className="w-full card rounded-3xl p-6 sm:p-8 relative overflow-hidden">
    <div className="absolute inset-0 dot-grid opacity-60" />
    <div className="relative">
      <div className="flex items-center gap-3 rounded-full bg-white border border-slate-200 px-4 py-3 shadow-sm">
        <Lock size={14} className="text-emerald-500" />
        <motion.span initial={{ width: 0 }} animate={{ width: "auto" }} transition={{ duration: 1, ease: EASE }} className="font-mono text-sm text-slate-700 overflow-hidden whitespace-nowrap">
          https://lowpages.site/p/oferta-setembro
        </motion.span>
      </div>
      <motion.div initial={{ opacity: 0, y: 30, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: 0.7, ease: EASE, duration: 0.8 }} className="mt-8 rounded-3xl bg-[var(--ink)] text-white p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-full bg-emerald-500 grid place-items-center"><Check size={20} /></span>
          <div>
            <div className="font-display font-bold text-2xl leading-none">Publicado</div>
            <div className="font-mono text-xs text-slate-400 mt-1">SSL ativo · CDN global · visitas ilimitadas</div>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-3 gap-3 text-center">
          {[["0", "linhas de código"], ["0", "configs de DNS"], ["1", "clique"]].map(([v, l]) => (
            <div key={l} className="rounded-2xl bg-white/5 p-3"><div className="font-display font-bold text-2xl text-[var(--orange)]">{v}</div><div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">{l}</div></div>
          ))}
        </div>
      </motion.div>
      {[...Array(8)].map((_, i) => (
        <motion.span key={i} initial={{ opacity: 0, y: 0 }} animate={{ opacity: [0, 1, 0], y: -90 - i * 8, x: (i - 4) * 26 }} transition={{ delay: 0.8 + i * 0.05, duration: 1.4, ease: "easeOut" }} className="absolute left-1/2 top-1/2 h-2 w-2 rounded-full" style={{ background: i % 2 ? "var(--orange)" : "var(--blue)" }} />
      ))}
    </div>
  </div>
);

const VISUALS = [VisualImport, VisualEdit, VisualPublish];

export const HowItWorks = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => setActive(Math.min(2, Math.max(0, Math.floor(v * 3)))));
  const Visual = VISUALS[active];

  return (
    <section id="como-funciona" ref={ref} data-testid="how-it-works-section" className="relative" style={{ height: "300vh" }}>
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <div className="mx-auto max-w-7xl w-full px-6 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-6">Como funciona</div>
            <h2 className="font-display font-bold tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl text-[var(--ink)]">
              Três passos.<br /><span className="text-slate-400">Zero infra.</span>
            </h2>
            <ol className="mt-10 flex flex-col gap-2">
              {STEPS.map((s, i) => {
                const on = i === active;
                return (
                  <li key={s.n} data-testid={`how-step-${i + 1}`} data-active={on} className={`relative rounded-2xl px-5 py-4 transition-colors duration-500 ${on ? "bg-white shadow-[var(--shadow)] border border-slate-100" : "opacity-50"}`}>
                    <div className="flex items-center gap-4">
                      <span className={`h-9 w-9 shrink-0 rounded-full grid place-items-center transition-colors duration-500 ${on ? "bg-[var(--blue)] text-white" : "bg-slate-100 text-slate-500"}`}><s.icon size={16} /></span>
                      <span className="font-mono text-xs text-slate-400">{s.n}</span>
                      <h3 className="font-display font-semibold text-lg text-[var(--ink)]">{s.title}</h3>
                    </div>
                    <AnimatePresence initial={false}>
                      {on && (
                        <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.5, ease: EASE }} className="overflow-hidden text-slate-600 text-sm sm:text-base leading-relaxed pl-[52px]">
                          <span className="block pt-3">{s.desc}</span>
                        </motion.p>
                      )}
                    </AnimatePresence>
                    {on && <motion.span layoutId="step-bar" className="absolute left-0 top-4 bottom-4 w-1 rounded-full bg-[var(--orange)]" />}
                  </li>
                );
              })}
            </ol>
          </div>
          <div className="lg:col-span-7 relative hidden sm:block" data-testid="how-visual-stage">
            <div className="absolute -inset-10 bg-[radial-gradient(ellipse_at_center,rgba(10,124,255,0.12),transparent_60%)]" />
            <AnimatePresence mode="wait">
              <motion.div key={active} initial={{ opacity: 0, y: 40, rotateX: 8 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} exit={{ opacity: 0, y: -30, rotateX: -6 }} transition={{ duration: 0.6, ease: EASE }} style={{ perspective: 1200 }} className="relative">
                <Visual />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
